import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";
import EntityConstants from "../../configs/EntityConstants";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import Dropdown from "../../common/Dropdown/Dropdown";
import VerificationApprovalsLongCard from "../../components/Cards/VerificationApprovalsLongCard/VerificationApprovalsLongCard";
import { useNavigate, useLocation } from "react-router-dom";
// import BulkVerificationApprovalModal from "../../common/BulkVerificationApprovalModal/BulkVerificationApprovalModal";
import CfLoader from "../../common/CfLoader/CfLoader";
import { fetchOrganisationsAPI } from "../../common/Organisations/fetchOrganisations";
import { fetchCurrentUser } from "../../service/CurrentUser";
import "react-datepicker/dist/react-datepicker.css";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { downloadFromUrl } from "../../service/DownloadFile";
import DateFilterDropdown from "../../components/DateFilterDropdown/DateFilterDropdown";
import { useTranslation } from "react-i18next";
import ExportIcon from "../../resources/icons/document-export.svg";

const SEARCH_DEBOUNCE_DELAY = 500;
const EXPORT_POLL_INTERVAL = 3000;

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const VerificationApprovals = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const APPROVAL_STATUS_MAP = {
    Approved: "Approved",
    Rejected: "Rejected",
    "Approval Pending": "Approval Pending",
  };

  const savedFilters = location.state?.filters || {};

  const limit = 6;
  const [respContentList, setRespContentList] = useState([]);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [search, setSearch] = useState(savedFilters.search || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [offset, setOffset] = useState(savedFilters.offset || 0);
  const [selectedAmountStatus, setSelectedAmountStatus] = useState(
    savedFilters.amountStatus || null,
  );
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState(
    hasOwn(savedFilters, "approvalStatus")
      ? savedFilters.approvalStatus
      : "Approval Pending",
  );
  const [selectedVerifierOrganisation, setSelectedVerifierOrganisation] =
    useState(savedFilters.verifierOrg || null);

  //! BULK ACTIONS - Can be resuable in future
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const [actionType, setActionType] = useState("");
  // const [selectedTransactions, setSelectedTransactions] = useState([]);
  // const [allSelected, setAllSelected] = useState(false);
  // const [issuerComments, setIssuerComments] = useState("");
  const [verifierOrganisations, setVerifierOrganisations] = useState([
    { name: t("verificationApprovals:all_verifier_organisations"), value: "" },
  ]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedDocumentType, setSelectedDocumentType] = useState(
    savedFilters.documentType,
  );
  const [organisationOptions, setOrganisationOptions] = useState([
    { name: t("verificationApprovals:all_organisations"), value: "" },
  ]);
  const [orgNext, setOrgNext] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgOffset, setOrgOffset] = useState(0);
  const [hasOrganisations, setHasOrganisations] = useState(false);
  const orgDropdownRef = useRef(null);
  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [org_code, setOrgCode] = useState("");
  const [userType, setUserType] = useState(null);
  const [tenantCode, setTenantCode] = useState("");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [dateRange, setDateRange] = useState("last6months");
  const [isExporting, setIsExporting] = useState(false);
  const isTenantRole = ["tenant_admin", "tenant_user"].includes(
    (userType ?? "").toLowerCase(),
  );

  const exportIntervalRef = useRef(null);

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString();
  };

  const getDateRangeForAPI = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "recent":
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
        break;
      case "last30days":
        startDate = subDays(startOfDay(now), 29);
        endDate = endOfDay(now);
        break;
      case "last3months":
        startDate = subDays(startOfDay(now), 89);
        endDate = endOfDay(now);
        break;
      case "last6months":
        startDate = subDays(startOfDay(now), 179);
        endDate = endOfDay(now);
        break;
      case "all":
        return { all_transactions: true };
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = startOfDay(customStartDate);
          endDate = endOfDay(customEndDate);
        } else {
          startDate = subDays(startOfDay(now), 6);
          endDate = endOfDay(now);
        }
        break;
      default:
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
    }

    return {
      from: formatDateForAPI(startDate),
      to: formatDateForAPI(endDate),
    };
  };

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchCurrentUser();
      if (data) {
        setUserType(data.type);
        setTenantCode(data.tenant_code);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (tenantCode && isTenantRole) {
      fetchOrgs();
    }
  }, [tenantCode]);

  useEffect(() => {
    if (!isInitialLoad) {
      GET_VERIFICATION_APPROVALS_LIST(limit, offset);
    }
  }, [org_code]);

  const fetchOrgs = async (loadMore = false) => {
    try {
      setOrgLoading(true);
      const offset = loadMore ? orgOffset : 0;
      const data = await fetchOrganisationsAPI(tenantCode, offset);

      if (!loadMore && data.options.length <= 1) {
        setHasOrganisations(false);
        setOrganisationOptions([
          { name: t("verificationApprovals:all_organisations"), value: "" },
        ]);
      } else {
        setHasOrganisations(true);
        if (loadMore) {
          const newOptions = data.options.filter((opt) => opt.value !== "");
          setOrganisationOptions((prev) => [...prev, ...newOptions]);
        } else {
          setOrganisationOptions(data.options);
        }
      }

      setOrgNext(data.next);
      setOrgOffset(
        offset + data.options.filter((opt) => opt.value !== "").length,
      );
    } catch (error) {
      console.error("Error fetching organisations:", error);
      setHasOrganisations(false);
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:failed_fetch_organisations"),
      });
    } finally {
      setOrgLoading(false);
    }
  };

  const handleOrgScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      orgNext &&
      !orgLoading
    ) {
      fetchOrgs(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target)
      ) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!orgDropdownOpen) {
      setOrgOffset(0);
    }
  }, [orgDropdownOpen]);

  const amountStatusOptions = useMemo(
    () => [
      { name: t("verificationApprovals:all_payment_status"), value: null },
      { name: t("verificationApprovals:paid"), value: "Paid" },
      { name: t("verificationApprovals:free"), value: "Free" },
    ],
    [t],
  );

  const approvalStatusOptions = useMemo(
    () => [
      { name: t("verificationApprovals:all_approval_status"), value: null },
      { name: t("verificationApprovals:approved"), value: "Approved" },
      { name: t("verificationApprovals:rejected"), value: "Rejected" },
      {
        name: t("verificationApprovals:approval_pending"),
        value: "Approval Pending",
      },
    ],
    [t],
  );

  const documentTypeOptions = useMemo(
    () => [
      { name: t("verificationApprovals:all_document_types"), value: null },
      { name: "VC", value: "VC" },
      { name: "Legacy", value: "Legacy" },
      { name: t("verificationApprovals:data"), value: "Data" },
      { name: "OCR", value: "OCR" },
    ],
    [t],
  );

  const updateAmountStatus = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedAmountStatus(value);
        setOffset(0);
      }
    },
    [isInitialLoad],
  );

  const updateDocumentType = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedDocumentType(value);
        setOffset(0);
      }
    },
    [isInitialLoad],
  );

  const updateApprovalStatus = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedApprovalStatus(value);
        setOffset(0);
      }
    },
    [isInitialLoad],
  );

  const updateVerifierOrganisation = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedVerifierOrganisation(value);
        setOffset(0);
      }
    },
    [isInitialLoad],
  );

  const handleRemoveTransaction = (transaction_id) => {
    setRespContentList((prevList) =>
      prevList.filter(
        (transaction) => transaction.transaction_id !== transaction_id,
      ),
    );
  };

  /* BULK ACTIONS - Can be resuable in future
  const handleCheckboxChange = (transactionId, isChecked) => {
    setSelectedTransactions((prevSelected) => {
      let newSelected;

      if (isChecked) {
        const transaction = respContentList.find(
          (t) => t.transaction_id === transactionId,
        );
        newSelected = [...prevSelected, transaction];
      } else {
        newSelected = prevSelected.filter(
          (t) => t.transaction_id !== transactionId,
        );
      }

      const currentPageIds = respContentList.map((t) => t.transaction_id);
      const selectedOnCurrentPage = newSelected.filter((t) =>
        currentPageIds.includes(t.transaction_id),
      );

      const allCurrentPageSelected =
        selectedOnCurrentPage.length === respContentList.length;
      setAllSelected(allCurrentPageSelected);

      return newSelected;
    });
  };

  const updateSelectedTransactions = useCallback(
    (updatedTransactions) => {
      setSelectedTransactions(updatedTransactions);

      if (respContentList.length > 0) {
        const currentPageIds = respContentList.map((t) => t.transaction_id);
        const selectedOnCurrentPage = updatedTransactions.filter((t) =>
          currentPageIds.includes(t.transaction_id),
        );

        const allCurrentPageSelected =
          selectedOnCurrentPage.length === respContentList.length;
        setAllSelected(allCurrentPageSelected);
      } else {
        setAllSelected(false);
      }
    },
    [respContentList],
  );

  const handleHeaderCheckboxChange = () => {
    if (allSelected) {
      setSelectedTransactions((prevSelected) =>
        prevSelected.filter(
          (t) =>
            !respContentList.some(
              (item) => item.transaction_id === t.transaction_id,
            ),
        ),
      );
    } else {
      setSelectedTransactions((prevSelected) => {
        const newSelections = respContentList.filter(
          (item) =>
            !prevSelected.some((t) => t.transaction_id === item.transaction_id),
        );
        return [...prevSelected, ...newSelections];
      });
    }
    setAllSelected(!allSelected);
  };

  const handleBulkReject = () => {
    if (selectedTransactions.length > 0) {
      setActionType("Rejected");
      setIsModalOpen(true);
    }
  };

  const handleBulkAccept = () => {
    if (selectedTransactions.length > 0) {
      setActionType("Approved");
      setIsModalOpen(true);
    }
  };

  const handleConfirmAction = async (
    actionType,
    transaction_id,
    comment = "",
  ) => {
    if (selectedTransactions.length > 0 || transaction_id !== undefined) {
      const body = {
        transaction_id:
          selectedTransactions.length > 0
            ? selectedTransactions.map(
              (transaction) => transaction.transaction_id,
            )
            : transaction_id,
        verification_approval_status: actionType,
        ...(comment?.trim() && { issuer_comments: comment.trim() }),
      };
      try {
        const response = await POST(
          EntityConstants.GET_VERIFICATION_APPROVALS_LIST,
          body,
        );

        if (response.ok) {
          Toast({
            type: "success",
            title: t("verificationApprovals:transaction_updated"),
            message: `${t(
              "verificationApprovals:transaction_has_been",
            )} ${actionType.toLowerCase()} ${t(
              "verificationApprovals:successfully",
            )}!`,
          });

          setSelectedTransactions([]);
          setAllSelected(false);
          setIssuerComments("");

          GET_VERIFICATION_APPROVALS_LIST(limit, offset);
        } else {
          const errorData = await response.json();
          Toast({
            type: "error",
            title: t("verificationApprovals:error"),
            message:
              errorData.message ||
              t("verificationApprovals:error_updating_transaction"),
          });
        }
      } catch (error) {
        console.error("Error during the POST request:", error);
        Toast({
          type: "error",
          title: "Error",
          message: t("verificationApprovals:error_updating_transaction_status"),
        });
      }
    }

    setIsModalOpen(false);
  };
  */

  const handleCardClick = (transaction) => {
    const currentFilters = {
      search,
      offset,
      amountStatus: selectedAmountStatus,
      approvalStatus: selectedApprovalStatus,
      verifierOrg: selectedVerifierOrganisation,
      documentType: selectedDocumentType,
      org_code,
    };
    const status =
      selectedApprovalStatus ||
      transaction.verification_approval_status ||
      "Approval Pending";

    navigate(`/issuer/approvals/${transaction.transaction_id}`, {
      state: {
        ...transaction,
        filters: currentFilters,
        status,
      },
    });
  };

  const fetchVerifierOrganisations = async () => {
    try {
      const url = org_code
        ? `${EntityConstants.GET_VERIFIER_ORGANISATIONS_LIST}?org_code=${org_code}`
        : EntityConstants.GET_VERIFIER_ORGANISATIONS_LIST;

      const response = await GET(url);
      const data = await response.json();

      if (data?.data) {
        setVerifierOrganisations([
          {
            name: t("verificationApprovals:verifier_organisation"),
            value: null,
          },
          ...data.data.map((org) => ({
            name: org.org_name,
            value: org.org_code,
          })),
        ]);
      }
    } catch (error) {
      console.error("Error fetching Verifier Organisations:", error);
    }
  };

  useEffect(() => {
    if (!isInitialLoad) {
      fetchVerifierOrganisations();
    }
  }, [org_code, t]);

  const handleExport = async () => {
    if (isExporting || dateRange === "all") return;

    setIsExporting(true);

    const dateRangeParams = getDateRangeForAPI();

    try {
      const payload = {
        ...(dateRangeParams.all_transactions && { all_transactions: true }),
        ...(dateRangeParams.from && { from: dateRangeParams.from }),
        ...(dateRangeParams.to && { to: dateRangeParams.to }),
        ...(org_code && { org_code }),

        ...(selectedVerifierOrganisation && {
          verifier_organisation: selectedVerifierOrganisation,
        }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(selectedAmountStatus && {
          payment_status: selectedAmountStatus.toLowerCase(),
        }),
        ...(selectedApprovalStatus && {
          verification_approval_status:
            APPROVAL_STATUS_MAP[selectedApprovalStatus],
        }),

        ...(selectedDocumentType && {
          document_type: selectedDocumentType,
        }),
      };

      const Response = await POST(
        EntityConstants.VERIFICATION_APPROVALS_EXPORT_EXCEL_CREATE,
        payload,
      );

      if (!Response.ok) {
        throw new Error("Failed to start export");
      }

      const startData = await Response.json();

      const { export_id } = startData;

      if (!export_id) {
        throw new Error("Export ID not received");
      }

      let pollCount = 0;
      const MAX_POLLS = 20;
      const pollStatus = async () => {
        try {
          const statusResponse = await GET(
            `${EntityConstants.VERIFICATION_APPROVALS_EXPORT_EXCEL}/${export_id}/status/`,
          );
          if (!statusResponse.ok) {
            throw new Error("Failed to fetch export status");
          }
          const statusData = await statusResponse.json();
          pollCount += 1;
          if (pollCount >= MAX_POLLS) {
            clearInterval(exportIntervalRef.current);
            exportIntervalRef.current = null;
            setIsExporting(false);
            Toast({
              type: "error",
              title: t("verificationApprovals:export_timeout"),
              message: t("verificationApprovals:export_took_too_long"),
            });

            return;
          }

          if (statusData.status === "COMPLETED") {
            clearInterval(exportIntervalRef.current);
            exportIntervalRef.current = null;
            setIsExporting(false);
            if (statusData.file_url) {
              downloadFromUrl(statusData.file_url, `data_${export_id}.xlsx`);
              Toast({
                type: "success",
                title: t("verificationApprovals:export_ready"),
                message: t("verificationApprovals:excel_file_ready_download"),
              });
            }
          }
        } catch (error) {
          clearInterval(exportIntervalRef.current);
          exportIntervalRef.current = null;
          setIsExporting(false);

          Toast({
            type: "error",
            title: t("verificationApprovals:export_failed"),
            message: t("verificationApprovals:unable_fetch_export_status"),
          });
        }
      };

      exportIntervalRef.current = setInterval(pollStatus, EXPORT_POLL_INTERVAL);
    } catch (error) {
      setIsExporting(false);
      Toast({
        type: "error",
        title: t("verificationApprovals:export_failed"),
        message: error.message || t("verificationApprovals:unable_export_data"),
      });
    }
  };

  const GET_VERIFICATION_APPROVALS_LIST = (limit, currentOffset) => {
    setIsSearching(true);
    const dateRangeParams = getDateRangeForAPI();
    const queryParams = new URLSearchParams({
      limit: String(limit),
      offset: String(currentOffset),
    });

    if (dateRangeParams.all_transactions) {
      queryParams.set("all_transactions", "true");
    } else {
      queryParams.set("from", dateRangeParams.from);
      queryParams.set("to", dateRangeParams.to);
    }

    if (selectedVerifierOrganisation) {
      queryParams.set("verifier_organisation", selectedVerifierOrganisation);
    }
    if (selectedAmountStatus) {
      queryParams.set("payment_status", selectedAmountStatus);
    }
    if (selectedDocumentType) {
      queryParams.set("document_type", selectedDocumentType);
    }
    if (selectedApprovalStatus) {
      queryParams.set("verification_approval_status", selectedApprovalStatus);
    }
    const trimmedSearchTerm = debouncedSearchTerm.trim();
    if (trimmedSearchTerm) {
      queryParams.set("search", trimmedSearchTerm);
    }
    if (org_code) {
      queryParams.set("org_code", org_code);
    }
    const apiUrl = `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}?${queryParams.toString()}`;
    const temp_array = respContentList;
    setRespContentList([]);

    GET(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: t("verificationApprovals:error"),
            message: data.error,
          });
          setRespContentList(temp_array);
        } else {
          setRespContentList(data.results);
          setPrevious(data.previous);
          setNext(data.next);

          /*
          const currentPageIds = data.results.map((t) => t.transaction_id);
          const filteredSelections = selectedTransactions.filter((t) =>
            currentPageIds.includes(t.transaction_id),
          );

          setSelectedTransactions(filteredSelections);

          if (data.results.length > 0) {
            const allCurrentPageSelected =
              filteredSelections.length === data.results.length;
            setAllSelected(allCurrentPageSelected);
          } else {
            setAllSelected(false);
          }
          */
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSearching(false);
        setIsSearched(true);
      });
  };

  //! BULK ACTIONS - Can be resuable in future
  // const refreshTable = useCallback(() => {
  //   GET_VERIFICATION_APPROVALS_LIST(limit, offset);
  // }, [limit, offset, GET_VERIFICATION_APPROVALS_LIST]);

  useEffect(() => {
    if (hasOwn(savedFilters, "approvalStatus")) {
      setSelectedApprovalStatus(savedFilters.approvalStatus);
    }
    if (savedFilters.documentType) {
      setSelectedDocumentType(savedFilters.documentType);
    }
    if (savedFilters.offset) {
      const savedOffset = parseInt(savedFilters.offset);
      setOffset(savedOffset);
    }
    if (savedFilters.org_code) {
      setOrgCode(savedFilters.org_code);
      const foundOrg = organisationOptions.find(
        (opt) => opt.value === savedFilters.org_code,
      );
      if (foundOrg) {
        setSelectedOrganisation(savedFilters.org_code);
      }
    }
    fetchVerifierOrganisations();
  }, [location.state]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(search);
    }, SEARCH_DEBOUNCE_DELAY);
    return () => {
      clearTimeout(handler);
      if (exportIntervalRef.current) {
        clearInterval(exportIntervalRef.current);
      }
    };
  }, [search]);

  useEffect(() => {
    GET_VERIFICATION_APPROVALS_LIST(limit, offset);

    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [
    debouncedSearchTerm,
    selectedAmountStatus,
    selectedApprovalStatus,
    selectedVerifierOrganisation,
    selectedDocumentType,
    limit,
    offset,
    dateRange,
    customStartDate,
    customEndDate,
  ]);

  const handlePrevious = () => {
    setOffset(offset - limit);
  };

  const handleNext = () => {
    setOffset(offset + limit);
  };

  const isExportDisabled =
    isExporting || respContentList.length === 0 || dateRange === "all";

  return (
    <div
      className="flex flex-col items-center w-full mt-20 bg-cfSecondary min-h-screen pb-3"
      id={pageRootTestIds.verificationApprovals}
    >
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <p className="text-cfHeadingText font-semibold self-start my-auto">
            {t("verificationApprovals:verification_approvals")}
          </p>
          <form
            onSubmit={(e) => e.preventDefault()}
            className=" bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center ml-[0rem] md:ml-[0.630rem] md:self-end md:m-0"
          >
            <input
              type="text"
              name="search"
              id="verification-approvals-input-search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              placeholder={t("verificationApprovals:search_organisation_name")}
              className="outline-none border-none p-0 placeholder:text-sm w-full"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>
      </div>

      <div className="w-full mt-[0.6rem] mb-[-0.6rem] px-4 md:px-8">
        <div className="flex flex-col xl:flex-row w-full items-stretch xl:items-center xl:justify-between gap-4">
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full min-w-0 sm:items-center">
            <div className="w-full sm:w-[13rem] min-w-0">
              <DateFilterDropdown
                dateRange={dateRange}
                setDateRange={setDateRange}
                customStartDate={customStartDate}
                setCustomStartDate={setCustomStartDate}
                customEndDate={customEndDate}
                setCustomEndDate={setCustomEndDate}
                testId="verification-approvals-date-filter"
              />
            </div>

            <div className="w-full sm:w-[15rem] min-w-0">
              <Dropdown
                options={verifierOrganisations}
                selected={
                  verifierOrganisations.find(
                    (option) => option.value === selectedVerifierOrganisation,
                  ) || verifierOrganisations[0]
                }
                onSelect={updateVerifierOrganisation}
                testId="verification-approvals-dropdown-verifier-org"
                buttonClass="px-4 py-2 border rounded-md w-full min-w-0"
                menuClass="z-20 w-full"
              />
            </div>

            <div className="w-full sm:w-[13rem] min-w-0">
              <Dropdown
                options={amountStatusOptions}
                selected={
                  amountStatusOptions.find(
                    (option) => option.value === selectedAmountStatus,
                  ) || amountStatusOptions[0]
                }
                onSelect={updateAmountStatus}
                testId="verification-approvals-dropdown-amount-status"
                buttonClass="px-4 py-2 border rounded-md w-full min-w-0"
                menuClass="z-20 w-full"
              />
            </div>

            <div className="w-full sm:w-[13rem] min-w-0">
              <Dropdown
                options={approvalStatusOptions}
                selected={
                  approvalStatusOptions.find(
                    (option) => option.value === selectedApprovalStatus,
                  ) || approvalStatusOptions[0]
                }
                onSelect={updateApprovalStatus}
                testId="verification-approvals-dropdown-approval-status"
                buttonClass="px-4 py-2 border rounded-md w-full min-w-0 items-center bg-cfSecondary"
                menuClass="z-50 w-full"
              />
            </div>

            <div className="w-full sm:w-[14rem] min-w-0">
              <Dropdown
                options={documentTypeOptions}
                selected={
                  documentTypeOptions.find(
                    (option) => option.value === selectedDocumentType,
                  ) || documentTypeOptions[0]
                }
                onSelect={updateDocumentType}
                testId="verification-approvals-dropdown-document-type"
                buttonClass="px-4 py-2 border rounded-md w-full min-w-0"
                menuClass="z-20 w-full"
              />
            </div>

            {isTenantRole && hasOrganisations && (
              <div
                className="relative w-full sm:w-[13rem] min-w-0"
                ref={orgDropdownRef}
              >
                <div
                  id="verification-approvals-dropdown-organisation-trigger"
                  className="flex justify-between items-center border-[1px] rounded-md p-[0.52rem] text-sm w-full cursor-pointer bg-cfSecondary"
                  onClick={() => {
                    setOrgDropdownOpen(!orgDropdownOpen);
                    if (!orgDropdownOpen) {
                      fetchOrgs();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setOrgDropdownOpen(!orgDropdownOpen);
                      if (!orgDropdownOpen) {
                        fetchOrgs();
                      }
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span className="truncate">
                    {selectedOrganisation
                      ? organisationOptions.find(
                          (opt) => opt.value === selectedOrganisation,
                        )?.name
                      : t("verificationApprovals:all_organisations")}
                  </span>
                  <svg
                    className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                      orgDropdownOpen ? "transform rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
                {orgDropdownOpen && (
                  <div
                    className="absolute z-50 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-cfSecondary rounded-md shadow-lg"
                    onScroll={handleOrgScroll}
                  >
                    {organisationOptions.length > 0 ? (
                      organisationOptions.map((option) => (
                        <div
                          key={option.value}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer truncate"
                          onClick={() => {
                            setSelectedOrganisation(option.value);
                            setOrgCode(option.value);
                            setOrgDropdownOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              setSelectedOrganisation(option.value);
                              setOrgCode(option.value);
                              setOrgDropdownOpen(false);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {option.name}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        {orgLoading
                          ? t("verificationApprovals:loading")
                          : t("verificationApprovals:no_organisations_found")}
                      </div>
                    )}
                    {orgLoading && (
                      <div className="px-4 py-2 flex justify-center">
                        {t("verificationApprovals:loading")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex w-full sm:w-auto xl:shrink-0">
            <button
              onClick={handleExport}
              disabled={isExportDisabled}
              id="verification-approvals-export"
              className={`flex w-full sm:min-w-[8rem] rounded-[8px] items-center justify-center gap-2 px-4 py-2 font-medium text-md whitespace-nowrap transition duration-200
    ${
      isExportDisabled
        ? "bg-indigo-300 cursor-not-allowed text-cfSecondary"
        : "bg-cfPrimaryButton text-cfSecondary hover:bg-cfPrimaryDark"
    }`}
            >
              {isExporting ? (
                <SmallCFLoader text={t("verificationApprovals:processing")} />
              ) : (
                <>
                  {t("verificationApprovals:export")}
                  <img src={ExportIcon} alt="Export" className="w-5 h-5" />
                </>
              )}
            </button>

            {/* BULK ACTIONS - Can be resuable in future
            <button
              className="flex-1 flex rounded-[8px] items-center justify-center gap-2 px-4 py-2 bg-cfPrimaryButton text-cfSecondary font-semibold text-md hover:bg-cfPrimaryDark transition duration-200"
              onClick={handleBulkAccept}
              disabled={selectedTransactions.length > 0 &&
                selectedApprovalStatus !== "Approved" &&
                selectedApprovalStatus !== "Rejected"}
            >
              {t("verificationApprovals:approve")}
            </button>
            <button
              className="flex-1 flex rounded-[8px] items-center justify-center gap-2 px-4 py-2 bg-cfSecondary text-black font-semibold text-md border border-gray-800 hover:bg-gray-100 transition duration-200"
              onClick={handleBulkReject}
              disabled={selectedTransactions.length > 0 &&
                selectedApprovalStatus !== "Approved" &&
                selectedApprovalStatus !== "Rejected"}
            >
              {t("verificationApprovals:reject")}
            </button>
            */}
          </div>
        </div>

        {/* BULK ACTIONS - Can be resuable in future
        <BulkVerificationApprovalModal
          isModalOpen={isModalOpen}
          toggleModal={setIsModalOpen}
          actionType={actionType}
          handleConfirmAction={() =>
            handleConfirmAction(actionType, undefined, issuerComments)
          }
          selectedTransactions={selectedTransactions}
          issuerComments={issuerComments}
          setIssuerComments={setIssuerComments}
          refreshTable={refreshTable}
          updateSelectedTransactions={updateSelectedTransactions}
        />
        */}
      </div>

      <div className="overflow-x-auto w-full px-4 md:px-8 mt-[-0.2rem]">
        <div className="flex flex-col justify-center gap-2 min-w-[73.5rem] md:w-full">
          <div className="flex bg-cfTableHeaderBackgroundColor items-center min-h-[5rem] border py-4 px-6 mt-6 ml-[0.05rem] mr-[0.05rem]">
            {/* BULK ACTIONS - Can be resuable in future
            <div className="flex w-[2.0%] justify-center items-center">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleHeaderCheckboxChange}
                className="cursor-pointer rounded-lg"
              />
            </div>
            */}
            {/* <div className="flex w-[2.0%] justify-center items-center"></div> */}
            <div className="flex w-[14.29%] justify-center items-center text-cfTableHeaderText font-bold text-[14px]">
              {t("verificationApprovals:verifier_name")}
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              {t("verificationApprovals:verifier_organisation")}
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              {t("verificationApprovals:created_at")}
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              {t("verificationApprovals:credissuer_verified")}
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              {t("verificationApprovals:amount_paid")}
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              {t("verificationApprovals:document_type")}
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              {t("verificationApprovals:actions")}
            </div>
          </div>

          {isSearching && <CfLoader />}
          {!isSearching &&
            isSearched &&
            respContentList.length > 0 &&
            respContentList.map((transaction) => (
              <VerificationApprovalsLongCard
                key={transaction.transaction_id}
                {...transaction}
                approval_status={transaction.approval_status}
                //! BULK ACTIONS - Can be resuable in future
                // onCheckboxChange={handleCheckboxChange}
                // isChecked={selectedTransactions.some(
                //   (t) => t.transaction_id === transaction.transaction_id,
                // )}
                // handleConfirmAction={handleConfirmAction}
                // selectedTransactions={selectedTransactions}
                onRemoveTransaction={handleRemoveTransaction}
                onClick={() => handleCardClick(transaction)}
              />
            ))}
          {!isSearching && isSearched && respContentList.length === 0 && (
            <p
              id="verification-approvals-no-results"
              className="text-center p-4 font-semibold"
            >
              {t("verificationApprovals:no_results_found")}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 px-8 w-full">
        <button
          onClick={handlePrevious}
          id="verification-approvals-pagination-previous"
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={previous === null}
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>

            <span>{t("verificationApprovals:previous")}</span>
          </span>
        </button>

        <button
          onClick={handleNext}
          id="verification-approvals-pagination-next"
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={next === null}
        >
          <span className="flex items-center justify-center gap-2">
            <span>{t("verificationApprovals:next")}</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default VerificationApprovals;
